'use client'

import { useMemo } from 'react'
import { birdieHistoryCopy } from 'src/components/BirdieRoundCard/birdieCopy'
import { getTranslatedMarbleColor } from 'src/services/i18n/utils/gameTranslations'
import type { Marble } from 'src/types/domain/Marble'
import type { Race } from 'src/types/domain/Race'
import './birdie-board.scss'
import { BirdieBallDefs } from './GolfBall'
import { birdiePastRounds } from './pastRounds'
import { PuttGlyph } from './PuttGlyph'
import { buildBirdieRecentForm, BIRDIE_FORM_WINDOW } from './recentForm'
import { formatMultiplierText } from './tileViewModel'
import { useBirdieRevealClock } from './useBirdieRevealClock'
import { useBirdieTranslate } from './useBirdieTranslate'

/**
 * Birdie's recent form (B19 section 5) — four rows, two figures.
 *
 * Owner sign-off 2026-09-08 on `birdie-round-card.tpl.html`: "love it, all
 * good"; and on the stats button specifically, the recommendation the mockup
 * carries — "keep it, and make it Birdie's Recent form … Same shape as the
 * other games' round stats, so a player who knows those reads this at once".
 *
 * So this is the shared `<RoundStats>` drawing with Birdie's own vocabulary: a
 * row per count option, its three balls as the label, a track bar, the count
 * and the percentage; then the average make chance and the biggest price that
 * paid.
 *
 * B19b makes that literal. This IS `<RoundStats>`' Birdie branch — the owner's
 * rule, "use what we already have … but just make a specific version for birdie
 * time" — so it is reached from the one place the other games' rows are, and
 * both of that component's mounts (the sidebar's top block and the stats
 * popover, compact) render it with the same window and the same `compact` prop.
 * B19's parallel `<BirdieStatsPopover>` is gone.
 *
 * WHAT IT SAYS is `recentForm.buildBirdieRecentForm`, which is pure and tested;
 * this file decides only how it looks.
 *
 * It always renders, even on a game with nothing settled yet: the rows are then
 * four honest zeros under a "Last 0 rounds" line, which is a truthful empty
 * state rather than a panel that opens on nothing. That is also why the Birdie
 * branch ignores `<RoundStats>`' loading skeleton — the window line already
 * reads whatever has landed, the store's ~10 rounds first and the deep read's 50
 * a moment later, and a skeleton for four rows of numbers costs more than it
 * saves.
 */

/** The row label's balls, px — the mockup's `.8cqw` at 12.8 px per cqw. */
const ROW_BALL_PX = 10.2

type Props = Readonly<{
  /** `<RoundStats>`' own window — already spoiler-filtered and settled-only by
   *  the caller. The panel takes its 50 rounds off the head of it. */
  races: readonly Race[]
  marbles: readonly Marble[]
  /** The floating panel's dress, exactly as `<RoundStats>` means it: drop the
   *  title, keep the window line. */
  compact?: boolean
}>

export function BirdieRecentForm({ races, marbles, compact }: Props) {
  const { t, translate, formatPercent, formatInteger } = useBirdieTranslate()
  const copy = useMemo(() => birdieHistoryCopy(translate), [translate])
  // ANTI-SPOILER CLOCK — a round joins the window only once its own reveal hold
  // has elapsed, exactly as the capsule withholds the final score.
  const nowMs = useBirdieRevealClock(races)
  const rounds = useMemo(
    () => birdiePastRounds({ races, marbles, nowMs, limit: BIRDIE_FORM_WINDOW }),
    [marbles, nowMs, races]
  )
  const view = useMemo(() => buildBirdieRecentForm(rounds, BIRDIE_FORM_WINDOW), [rounds])

  return (
    <section aria-label={copy.recentForm} className="birdie-form">
      {/* Both mounts (the sidebar's top block and the stats popover) are outside
          `.birdie-overlay`, so the row labels' balls need their gradients
          declared here or they draw unpainted (`GolfBall.tsx`). */}
      <BirdieBallDefs />
      <div className="birdie-form__head">
        {compact ? null : <span className="birdie-form__title">{copy.recentForm}</span>}
        <span className="birdie-form__window">{copy.window(view.sampleSize)}</span>
      </div>

      {/* `role="list"` explicitly: Safari drops the implicit list role from a
          `<ul>` the moment `list-style: none` is applied, which is exactly what
          this block does. */}
      {/* eslint-disable-next-line jsx-a11y/no-redundant-roles -- NOT redundant in
          Safari: it drops a <ul>'s implicit list role the moment `list-style:
          none` is applied, which is exactly what these rows do. */}
      <ul className="birdie-form__rows" role="list">
        {view.rows.map((row) => (
          <li className="birdie-form__row" key={row.key}>
            {/* The row's NAME is the count option's own marbleColors entry —
                the same words the board's bars and the round card's outcome
                line use, so one outcome can never be called two things. It is
                screen-reader-only because the balls beside it say it visually;
                the sr-only span is absolutely positioned and takes no column. */}
            <span className="birdie-form__marks">
              {row.marks.map((mark, index) => (
                // Fixed length, immutable order: a count option's balls are
                // decided by its key and cannot change under the index.
                // eslint-disable-next-line react/no-array-index-key -- fixed-length, immutable
                <PuttGlyph key={index} mark={mark} size={ROW_BALL_PX} />
              ))}
              <span className="birdie-sr-only">
                {getTranslatedMarbleColor(row.key, translate)}
              </span>
            </span>
            <span aria-hidden="true" className="birdie-form__track">
              <span
                className="birdie-form__fill"
                style={{ width: `${Math.round(row.fraction * 10000) / 100}%` }}
              />
            </span>
            <span className="birdie-form__val">
              {formatInteger(row.count)}
              <small>{formatPercent(row.fraction)}</small>
            </span>
          </li>
        ))}
      </ul>

      <div className="birdie-form__foot">
        <span>
          {copy.avgMakeChance}
          <b>{formatPercent(view.avgMakeRate)}</b>
        </span>
        {view.biggestPaid === undefined ? null : (
          <span>
            {copy.biggestPaid}
            <b className="birdie-form__gold">{formatMultiplierText(view.biggestPaid)}</b>
          </span>
        )}
      </div>

      {/* The line the shared drawing closes on, in its non-compact (sidebar)
          dress, and the shared class so it is set the same way. It is a
          responsible-play statement, not decoration — a Birdie player reading
          four rows of frequencies is being told the same thing every other
          game's player is told under the same rows. The key is the shared one
          and already exists in all sixteen locales. */}
      {compact ? null : (
        <p className="round-stats__note">{t('Past rounds do not predict future rounds.')}</p>
      )}
    </section>
  )
}
